/**
 * API 공통 (Todak api/client.js 와 같은 모양, AGENTS.md 5장).
 *
 * - 기본 주소: VITE_API_URL, 없으면 같은 출처의 /api (개발에서는 Vite 가 서버로 넘깁니다)
 * - 인증: Authorization: Bearer <세션 토큰> (귀인도감 방식)
 * - 시간 제한: 기본 15초. 부르는 쪽의 signal 로도 취소됩니다
 * - 재시도: GET 만, 네트워크 오류와 5xx 에서 두 번까지. 다른 메서드는 다시 보내지 않습니다
 *   (저장이 두 번 될 수 있습니다)
 * - 401 · USER_NOT_FOUND 는 setOnAuthError 로 건 처리(로그인 화면으로)를 부릅니다
 */
export const API_BASE = import.meta.env.VITE_API_URL ?? '/api';
const DEFAULT_TIMEOUT_MS = 15000;
const GET_RETRIES = 2;
const RETRY_DELAY_MS = 300;

export class ApiError extends Error {
    constructor(status, body) {
        super(body?.error ?? `API ${status}`);
        this.name = 'ApiError';
        this.status = status;
        this.code = body?.code ?? null;
        this.details = body?.details ?? null;
        this.body = body;
    }
}

/** 화면에 보여 줄 문구. 서버가 준 문구가 있으면 그것을, 없으면 상황에 맞는 기본 문구를 씁니다. */
export function errorMessageOf(error, fallback = '잠시 후 다시 시도해 주세요.') {
    if (error instanceof ApiError && error.status < 500 && error.message) {
        return error.message;
    }
    if (error?.name === 'TimeoutError' || error?.name === 'NetworkError') {
        return '연결이 원활하지 않아요. 잠시 후 다시 시도해 주세요.';
    }
    return fallback;
}

export function isAbortError(error) {
    return error?.name === 'AbortError';
}

let getToken = () => null;
let onAuthError = null;

/** 세션 저장소가 토큰을 읽는 길과 인증이 끊겼을 때의 처리를 겁니다 (stores/session.js). */
export function configureApi({ tokenGetter, authErrorHandler }) {
    getToken = tokenGetter;
    onAuthError = authErrorHandler;
}

/** 스트리밍 요청(api/chatStream.js)이 같은 인증 머리글을 쓰도록 내놓습니다 */
export function authHeaders() {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

/** 스트리밍 요청에서 세션이 끊겼을 때도 같은 처리를 합니다 */
export function notifyAuthError(error) {
    onAuthError?.(error);
}

function wait(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

async function sendOnce(path, { method, body, signal, timeoutMs }) {
    const headers = { Accept: 'application/json' };
    const token = getToken();
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    if (body !== undefined) {
        headers['Content-Type'] = 'application/json';
    }
    const timeoutSignal = AbortSignal.timeout(timeoutMs);
    const combinedSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;

    let response;
    try {
        response = await fetch(`${API_BASE}${path}`, {
            method,
            headers,
            body: body === undefined ? undefined : JSON.stringify(body),
            signal: combinedSignal,
        });
    } catch (error) {
        if (signal?.aborted) {
            throw error;
        }
        if (timeoutSignal.aborted) {
            const timeoutError = new Error('응답이 늦어요.');
            timeoutError.name = 'TimeoutError';
            throw timeoutError;
        }
        const networkError = new Error('네트워크에 닿지 않아요.', { cause: error });
        networkError.name = 'NetworkError';
        throw networkError;
    }

    if (response.status === 204) {
        return null;
    }
    const text = await response.text();
    let json = null;
    if (text) {
        try {
            json = JSON.parse(text);
        } catch {
            // 서버가 JSON 이 아닌 것을 돌려줬습니다(프록시 오류 화면 등). 본문은 오류에 그대로 싣습니다.
            json = { error: null, raw: text.slice(0, 200) };
        }
    }
    if (!response.ok) {
        throw new ApiError(response.status, json);
    }
    return json;
}

function isRetryable(error) {
    if (error instanceof ApiError) {
        return error.status >= 500;
    }
    return error?.name === 'NetworkError' || error?.name === 'TimeoutError';
}

/**
 * @param {string} path  '/auth/me' 처럼 /api 뒤의 경로
 * @param {{ method?: string, body?: any, signal?: AbortSignal, timeoutMs?: number, skipAuthHandler?: boolean }} options
 */
export async function api(path, options = {}) {
    const method = options.method ?? 'GET';
    const retries = method === 'GET' ? GET_RETRIES : 0;
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

    for (let attempt = 0; ; attempt += 1) {
        try {
            return await sendOnce(path, { method, body: options.body, signal: options.signal, timeoutMs });
        } catch (error) {
            const isAuthError =
                error instanceof ApiError &&
                (error.status === 401 || error.code === 'AUTH_REQUIRED' || error.code === 'USER_NOT_FOUND');
            if (isAuthError && !options.skipAuthHandler) {
                onAuthError?.(error);
            }
            if (attempt >= retries || !isRetryable(error) || options.signal?.aborted) {
                throw error;
            }
            await wait(RETRY_DELAY_MS * (attempt + 1));
        }
    }
}
